import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkReviewBillingReconciliationFileHealth,
  listBillingReconciliationFileHealth,
  refreshBillingReconciliationFileHealth,
  reviewBillingReconciliationFileHealth,
  summarizeBillingReconciliationFileHealth,
} from './billingReconciliationApi.js';
import {
  invalidateBillingBacContext,
  scheduleBillingQueryInvalidations,
} from './billingReconciliationQueryInvalidation.js';

const fileHealthKeys = {
  list: ({ runId, bac, tier, search, openOnly, limit, offset }) => [
    'billingRecon',
    'fileHealth',
    runId || null,
    bac || null,
    tier || null,
    search || null,
    openOnly ?? null,
    limit || null,
    offset || null,
  ],
  summary: ({ runId, bac, tier, summaryMode, summary_mode } = {}) => [
    'billingRecon',
    'fileHealthSummary',
    runId || null,
    bac || null,
    tier || null,
    summaryMode || summary_mode || null,
  ],
};

const listRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  const rows = payload?.rows || [];
  if (Array.isArray(rows) && payload && typeof payload === 'object') {
    Object.defineProperty(rows, 'meta', {
      value: {
        total: payload.total,
        returned: payload.returned,
        limit: payload.limit,
        offset: payload.offset,
        hasMore: payload.hasMore,
      },
      enumerable: false,
      configurable: true,
    });
  }
  return rows;
};

const noRetry = { retry: false };

const rowsFromPayload = (payload) => (Array.isArray(payload) ? payload : payload?.rows || []);
const withRows = (payload, rows) => {
  if (Array.isArray(payload)) return rows;
  if (payload && typeof payload === 'object') {
    return {
      ...payload,
      rows,
      total: payload.total,
      returned: payload.returned !== undefined ? rows.length : payload.returned,
    };
  }
  return payload;
};
const fileHealthReviewedIds = (data, variables = {}) => {
  const ids = new Set([data?.id, data?.findingId, ...(data?.ids || []), ...(variables?.ids || []), ...(variables?.findingIds || [])]
    .map((value) => String(value || '').trim())
    .filter(Boolean));
  return ids;
};
const codeSet = (data, variables = {}) => new Set([data?.code, ...(data?.codes || []), variables?.code, ...(variables?.codes || [])]
  .map((value) => String(value || '').trim().toUpperCase())
  .filter(Boolean));
const queryKeyMatchesReviewScope = (queryKey = [], variables = {}) => {
  const [, domain, runId, bac, tier] = queryKey;
  if (domain !== 'fileHealth' && domain !== 'fileHealthSummary') return false;
  if (variables?.runId && runId !== variables.runId) return false;
  if (variables?.bac && bac && String(bac) !== String(variables.bac)) return false;
  if (variables?.tier && tier && String(tier) !== String(variables.tier)) return false;
  if (!variables?.tier && tier) return false;
  return true;
};
const matchesReviewedFileHealth = (row, ids, codes, variables = {}) => {
  const rowId = String(row?.id || row?.findingId || '').trim();
  if (rowId && ids.has(rowId)) return true;
  const rowCode = String(row?.code || '').trim().toUpperCase();
  if (rowCode && codes.has(rowCode)) {
    const bac = String(variables?.bac || '').trim();
    return !bac || String(row?.bac || '').trim() === bac;
  }
  return false;
};
const removeReviewedFileHealthCaches = (queryClient, data, variables = {}) => {
  const ids = fileHealthReviewedIds(data, variables);
  const codes = codeSet(data, variables);
  if (!ids.size && !codes.size) return;
  queryClient.getQueryCache()
    .findAll({ queryKey: ['billingRecon', 'fileHealth'] })
    .forEach((query) => {
      if (!queryKeyMatchesReviewScope(query.queryKey, variables)) return;
      queryClient.setQueryData(query.queryKey, (current) => {
        const rows = rowsFromPayload(current);
        if (!rows.length) return current;
        return withRows(current, rows.filter((row) => !matchesReviewedFileHealth(row, ids, codes, variables)));
      });
    });
};
const decrementSummaryCount = (value, amount) => {
  if (!Number.isFinite(Number(value))) return value;
  return Math.max(0, Number(value) - amount);
};
const adjustFileHealthSummaryCaches = (queryClient, data, variables = {}) => {
  const reviewedCount = Number(data?.reviewedCount || data?.ids?.length || 1);
  if (!Number.isFinite(reviewedCount) || reviewedCount <= 0) return;
  const codes = codeSet(data, variables);
  queryClient.getQueryCache()
    .findAll({ queryKey: ['billingRecon', 'fileHealthSummary'] })
    .forEach((query) => {
      if (!queryKeyMatchesReviewScope(query.queryKey, variables)) return;
      queryClient.setQueryData(query.queryKey, (current) => {
        if (!current || typeof current !== 'object') return current;
        const next = {
          ...current,
          open: decrementSummaryCount(current.open, reviewedCount),
          addressed: Number.isFinite(Number(current.addressed)) ? Number(current.addressed) + reviewedCount : current.addressed,
        };
        if (next.byCode && codes.size) {
          next.byCode = next.byCode.map((row) => {
            if (!codes.has(String(row.code || '').trim().toUpperCase())) return row;
            const amount = Math.min(Number(row.open || 0), reviewedCount);
            return {
              ...row,
              open: decrementSummaryCount(row.open, amount),
              addressed: Number.isFinite(Number(row.addressed)) ? Number(row.addressed) + amount : row.addressed,
            };
          });
        }
        return next;
      });
    });
};
const invalidateFileHealthReviewDependents = (queryClient, variables = {}) => {
  scheduleBillingQueryInvalidations(queryClient, [
    ['billingRecon', 'fileHealth'],
    ['billingRecon', 'fileHealthSummary'],
  ], { refetchType: 'active' });
  if (variables?.runId) {
    scheduleBillingQueryInvalidations(queryClient, [
      ['billingRecon', 'varianceCompletionReadiness', variables.runId],
      ['billingRecon', 'bacContext', variables.runId],
      ['billingRecon', 'operatorContext', variables.runId],
    ]);
  } else {
    scheduleBillingQueryInvalidations(queryClient, [
      ['billingRecon', 'bacContext'],
      ['billingRecon', 'operatorContext'],
    ]);
  }
};

export const useBillingReconciliationFileHealth = (params = {}, options = {}) =>
  useQuery({
    queryKey: fileHealthKeys.list(params),
    queryFn: ({ signal }) => listBillingReconciliationFileHealth({ ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId),
    select: listRows,
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useBillingReconciliationFileHealthSummary = (params = {}, options = {}) =>
  useQuery({
    queryKey: fileHealthKeys.summary(params),
    queryFn: ({ signal }) => summarizeBillingReconciliationFileHealth({ ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId),
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useRefreshBillingReconciliationFileHealth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshBillingReconciliationFileHealth,
    onSuccess: (_data, variables) => {
      if (variables?.runId) {
        queryClient.invalidateQueries({ queryKey: ['billingRecon', 'fileHealth', variables.runId] });
        queryClient.invalidateQueries({ queryKey: ['billingRecon', 'fileHealthSummary', variables.runId] });
        invalidateBillingBacContext(queryClient, { runId: variables.runId });
      } else {
        queryClient.invalidateQueries({ queryKey: ['billingRecon', 'fileHealth'] });
        queryClient.invalidateQueries({ queryKey: ['billingRecon', 'fileHealthSummary'] });
        invalidateBillingBacContext(queryClient);
      }
    },
  });
};

export const useReviewBillingReconciliationFileHealth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reviewBillingReconciliationFileHealth,
    onSuccess: (data, variables) => {
      removeReviewedFileHealthCaches(queryClient, data, variables);
      adjustFileHealthSummaryCaches(queryClient, data, variables);
      invalidateFileHealthReviewDependents(queryClient, variables);
    },
  });
};

export const useBulkReviewBillingReconciliationFileHealth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkReviewBillingReconciliationFileHealth,
    onSuccess: (data, variables) => {
      removeReviewedFileHealthCaches(queryClient, data, variables);
      adjustFileHealthSummaryCaches(queryClient, data, variables);
      invalidateFileHealthReviewDependents(queryClient, variables);
    },
  });
};
